import React, { useMemo, useState } from "react";

export default function OrderDetails({
  order,
  completedRemovalUnlocked = false,
}) {
  const [error, setError] = useState("");

  const formatPickupDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatPickupTime = (timeStr) => {
    if (!timeStr) return "";
    try {
      if (timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) return timeStr;
      const [hour, minute] = timeStr.split(":");
      let h = parseInt(hour, 10);
      const ampm = h >= 12 ? "pm" : "am";
      if (h > 12) h -= 12;
      if (h === 0) h = 12;
      return `${h}:${minute} ${ampm}`;
    } catch {
      return timeStr;
    }
  };



  const isDeliveryOrder =
    String(order?.order_type || "")
      .trim()
      .toLowerCase() === "delivery";



  const normalizedItems = useMemo(() => {
    if (!order?.items) return [];
    if (typeof order.items === "string") {
      try {
        const parsed = JSON.parse(order.items);
        return Array.isArray(parsed) ? parsed : Object.values(parsed);
      } catch (e) {
        return [];
      }
    }
    return Array.isArray(order.items) ? order.items : Object.values(order.items);
  }, [order?.items]);



  return (
    <>
      <div className="space-y-2 text-sm text-slate-700">
        <div className="space-y-2">
          {order.customer_phone && (
            <p>
              <strong>Phone No:</strong> {order.customer_phone}
            </p>
          )}

          {order.customer_email && (
            <p>
              <strong>Email:</strong> {order.customer_email}
            </p>
          )}

          {order.pickup_date && (
            <p>
              <strong>
                {String(order.order_type || "").trim().toLowerCase() === "eat in" ? "Eat In" : "Pick Up"} Date:
              </strong>{" "}
              {formatPickupDate(order.pickup_date)}
            </p>
          )}

          {order.pickup_time && (
            <p>
              <strong>
                {String(order.order_type || "").trim().toLowerCase() === "eat in" ? "Eat In" : "Pick Up"} Time:
              </strong>{" "}
              {formatPickupTime(order.pickup_time)}
            </p>
          )}

          {isDeliveryOrder && (
            <>
              {(order.delivery_address || order.postcode) && (
                <p>
                  <strong>Delivery Address:</strong>{" "}
                  {typeof order.delivery_address === "object"
                    ? [
                        order.delivery_address?.line1,
                        order.delivery_address?.line2,
                        order.delivery_address?.city,
                        order.delivery_address?.postcode || order.postcode,
                      ]
                        .filter(Boolean)
                        .join(", ")
                    : [order.delivery_address, order.postcode].filter(Boolean).join(", ")}
                </p>
              )}

              {order.delivery_fee != null && (
                <p>
                  <strong>Delivery Fee:</strong> £
                  {parseFloat(order.delivery_fee).toFixed(2)}
                </p>
              )}

              {order.service_charge != null && order.service_charge > 0 && (
                <p>
                  <strong>Service Charge:</strong> £
                  {parseFloat(order.service_charge).toFixed(2)}
                </p>
              )}


            </>
          )}
        </div>

        <hr className="border-slate-200" />

        <div className="space-y-2">
          {normalizedItems.map((item) => {
            const mainProteinIds = [103, 104, 105, 106, 107, 108];

            const mainProteins =
              item.proteins?.filter((p) =>
                mainProteinIds.includes(Number(p.id)),
              ) || [];

            const extraProteins =
              item.proteins?.filter(
                (p) => !mainProteinIds.includes(Number(p.id)),
              ) || [];

            const optionTotal = [
              ...(item.selectedOptionalOptions || []),
              ...(item.swallowOptions || []),
              ...(item.proteins || []),
            ].reduce((sum, opt) => sum + opt.price * opt.quantity, 0);

            const currentPrice = item.price !== undefined && item.price !== null ? parseFloat(item.price) : parseFloat(item.base_price || 0);

            const itemTotal =
              (currentPrice +
                parseFloat(item.required_option_price || 0) +
                optionTotal) *
              (item.quantity || 1);

            const isRemoved =
              Number(item.is_removed) === 1 || item.is_removed === true;

            return (
              <div
                key={item.id}
                className={`relative rounded-2xl p-3 ring-1 transition ${
                  isRemoved
                    ? "bg-slate-100/90 opacity-60 ring-slate-300"
                    : "bg-white ring-slate-200"
                }`}
              >
                {isRemoved && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg">
                      <i className="fa-solid fa-ban text-xl"></i>
                    </div>
                  </div>
                )}

                <div className="mb-1 flex items-start justify-between gap-3">
                  <span
                    className={`text-md font-bold ${
                      isRemoved
                        ? "text-slate-400 line-through"
                        : "text-slate-900"
                    }`}
                  >
                    {item.name} (£{(item.price !== undefined && item.price !== null ? parseFloat(item.price) : parseFloat(item.base_price || 0)).toFixed(2)})
                  </span>

                </div>

                {item.required_option_id && (
                  <div className="mb-2">
                    <ul className="list-disc space-y-1 pl-5 text-md">
                      <li className={isRemoved ? "text-slate-400" : ""}>
                        <span className={isRemoved ? "line-through" : ""}>
                          {item.required_option_name}
                        </span>{" "}
                        <span className="text-lg font-bold text-red-500">
                          x {item.quantity}
                        </span>{" "}
                        (£
                        {(
                          parseFloat(item.required_option_price || 0) *
                          item.quantity
                        ).toFixed(2)}
                        )
                      </li>
                    </ul>
                  </div>
                )}

                {(item.selectedOptionalOptions || []).length > 0 && (
                  <div className="mb-2">
                    <strong className={isRemoved ? "text-slate-400" : ""}>
                      Optional:
                    </strong>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-md">
                      {item.selectedOptionalOptions.map((opt, idx) => (
                        <li
                          key={idx}
                          className={isRemoved ? "text-slate-400" : ""}
                        >
                          <span className={isRemoved ? "line-through" : ""}>
                            {opt.name}
                          </span>{" "}
                          <span className="text-lg font-bold text-red-500">
                            x {opt.quantity}
                          </span>{" "}
                          (£{(opt.price * opt.quantity).toFixed(2)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(item.swallowOptions || []).length > 0 && (
                  <div className="mb-2">
                    <strong className={isRemoved ? "text-slate-400" : ""}>
                      Swallow:
                    </strong>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-md">
                      {item.swallowOptions.map((sw, idx) => (
                        <li
                          key={idx}
                          className={isRemoved ? "text-slate-400" : ""}
                        >
                          <span className={isRemoved ? "line-through" : ""}>
                            {sw.name}
                          </span>{" "}
                          <span className="text-lg font-bold text-red-500">
                            x {sw.quantity}
                          </span>{" "}
                          (£{(sw.price * sw.quantity).toFixed(2)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {mainProteins.length > 0 && (
                  <div className="mb-2">
                    <strong className={isRemoved ? "text-slate-400" : ""}>
                      Protein(s):
                    </strong>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-md">
                      {mainProteins.map((p, idx) => (
                        <li
                          key={idx}
                          className={isRemoved ? "text-slate-400" : ""}
                        >
                          <span className={isRemoved ? "line-through" : ""}>
                            {p.name}
                          </span>{" "}
                          <span className="text-lg font-bold text-red-500">
                            x {p.quantity}
                          </span>{" "}
                          (£{(p.price * p.quantity).toFixed(2)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {extraProteins.length > 0 && (
                  <div className="mb-2">
                    <strong className={isRemoved ? "text-slate-400" : ""}>
                      Extra:
                    </strong>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-md">
                      {extraProteins.map((p, idx) => (
                        <li
                          key={idx}
                          className={isRemoved ? "text-slate-400" : ""}
                        >
                          <span className={isRemoved ? "line-through" : ""}>
                            {p.name}
                          </span>{" "}
                          <span className="text-lg font-bold text-red-500">
                            x {p.quantity}
                          </span>{" "}
                          (£{(p.price * p.quantity).toFixed(2)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-3 flex justify-end border-t border-slate-200 pt-3 text-base">
                  <span
                    className={`font-bold ${
                      isRemoved
                        ? "text-slate-400 line-through"
                        : "text-slate-900"
                    }`}
                  >
                    <span className="text-lg font-extrabold text-green-700 drop-shadow-sm">
                      x {item.quantity}
                    </span>{" "}
                    = £{itemTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <hr className="border-slate-200" />



        {order.notes?.trim() && (
          <p className="text-base text-lg font-bold text-red-500">
            <strong>Notes:</strong> <span>{order.notes}</span>
          </p>
        )}
      </div>

      {error && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 px-4 backdrop-blur-sm"
          onClick={() => setError("")}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-rose-200 bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600">
                <i className="fas fa-exclamation-triangle"></i>
              </div>

              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">Error</p>
                <p className="mt-1 text-sm text-slate-600">{error}</p>

                <button
                  onClick={() => setError("")}
                  className="mt-3 rounded-lg bg-rose-600 px-3 py-1 text-sm text-white hover:bg-rose-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
